/*
 * In-app notifications, the bell (docs/notifications.md; docs/api.md, "In-app
 * notifications (the bell)"): GET /me/inbox and POST /me/inbox/read, kept live
 * by the `notification` event on the org's event stream (api/live.ts). The
 * email log stays at /me/notifications and isn't read here.
 *
 * An API without the inbox answers 404; the bell then stays hidden (null)
 * rather than showing an empty or made-up list.
 */
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, isApiError } from "./client";
import { onNotificationEvent, type NotificationEvent } from "./live";

export type NotificationSeverity = "urgent" | "action" | "info";

export type AppNotification = {
  id: string;
  /** An alert kind (backup_failed, …) or an in-app-only one (site_down, security_updates, …). */
  kind: string;
  severity: NotificationSeverity;
  /** "Backup of Acme (Live) failed" */
  title: string;
  /** One plain line. */
  detail: string | null;
  /** A panel path for its one action: /o/<slug>/sites/<id>?tab=backups */
  link: string | null;
  /** The action's words ("Open the backup"); "Open" when absent. */
  action?: string | null;
  /** Buttons beyond the link: an API call ("Restart now") or a page ("Move it"). [] for most. */
  actions?: NotificationAction[];
  /** The org it's in (links go there). */
  org?: { id: string; slug: string; name: string } | null;
  site?: { id: string; name: string } | null;
  environment?: { id: string; name: string } | null;
  server?: { id: string; name: string } | null;
  domain?: { id: string | null; name: string } | null;
  createdAt: string;
  updatedAt: string;
  readAt: string | null;
  /** The same alert for the same thing within 24 hours: "(3 times)". */
  repeats: number;
};

/** `confirm`: what the confirm dialog says before an API call ("Sites on test-1 will be unreachable for about 20 seconds."). */
export type NotificationAction = { label: string; api?: { method: "POST" | "PATCH"; path: string; body?: object }; link?: string; confirm?: string };

export type NotificationsPage = { notifications: AppNotification[]; next?: string | null; unread: number; urgentUnread: number };

export const NOTIFICATIONS_KEY = ["me", "inbox"] as const;

/** The newest notifications, or null when the API has no inbox. */
export function useNotifications() {
  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: async ({ signal }) => {
      try {
        const res = await api.get<NotificationsPage>("/me/inbox?limit=50", signal);
        return { notifications: res.notifications ?? [], next: res.next ?? null, unread: res.unread ?? 0, urgentUnread: res.urgentUnread ?? 0 } satisfies NotificationsPage;
      } catch (e) {
        if (isApiError(e) && (e.status === 404 || e.status === 501)) return null;
        throw e;
      }
    },
    retry: (n, e) => !(isApiError(e) && e.status < 500) && n < 2,
    staleTime: 60_000,
  });
}

/** Mark some (or all) as read. The answer carries the new counts. */
export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { ids: string[] } | { all: true }) => api.post<{ unread: number; urgentUnread: number }>("/me/inbox/read", v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: NOTIFICATIONS_KEY });
      const prev = qc.getQueryData<NotificationsPage | null>(NOTIFICATIONS_KEY);
      markRead(qc, "all" in v ? "all" : v.ids);
      return { prev };
    },
    onSuccess: (counts) => setCounts(qc, counts),
    onError: (_e, _v, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(NOTIFICATIONS_KEY, ctx.prev);
    },
  });
}

function markRead(qc: QueryClient, which: string[] | "all") {
  const prev = qc.getQueryData<NotificationsPage | null>(NOTIFICATIONS_KEY);
  if (!prev) return;
  const now = new Date().toISOString();
  const notifications = prev.notifications.map((n) => (!n.readAt && (which === "all" || which.includes(n.id)) ? { ...n, readAt: now } : n));
  qc.setQueryData<NotificationsPage>(NOTIFICATIONS_KEY, {
    ...prev,
    notifications,
    unread: notifications.filter((n) => !n.readAt).length,
    urgentUnread: notifications.filter((n) => !n.readAt && n.severity === "urgent").length,
  });
}

function setCounts(qc: QueryClient, c: { unread: number; urgentUnread: number }) {
  const prev = qc.getQueryData<NotificationsPage | null>(NOTIFICATIONS_KEY);
  if (prev) qc.setQueryData<NotificationsPage>(NOTIFICATIONS_KEY, { ...prev, unread: c.unread, urgentUnread: c.urgentUnread });
}

/**
 * Keeps the bell current from the live stream: a new or updated row goes to the top, rows read in
 * another tab are marked, and the counts are the server's. `onUrgent` hears about a new urgent one.
 */
export function useNotificationsLive(onUrgent?: (n: AppNotification) => void) {
  const qc = useQueryClient();
  useEffect(
    () =>
      onNotificationEvent((ev: NotificationEvent) => {
        const prev = qc.getQueryData<NotificationsPage | null>(NOTIFICATIONS_KEY);
        if (ev.notification && prev) {
          const n = ev.notification as AppNotification;
          const isNew = !prev.notifications.some((x) => x.id === n.id && x.updatedAt === n.updatedAt);
          qc.setQueryData<NotificationsPage>(NOTIFICATIONS_KEY, { ...prev, notifications: [n, ...prev.notifications.filter((x) => x.id !== n.id)] });
          if (isNew && !n.readAt && n.severity === "urgent") onUrgent?.(n);
        } else if (ev.notification && !prev) {
          void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
        }
        if (ev.read) markRead(qc, ev.read);
        setCounts(qc, ev);
      }),
    [qc, onUrgent],
  );
}
